import { NextRouter } from 'next/router';
import {
  IAnsweredItem,
  IAnsweredShippingAddress,
  IMergedAnsweredItem,
  IQuotationAnswer,
  IShippingAddress,
} from '@/types';
import { ILocation, LocationType } from '@/types/api/locations';
import { User } from '@/types/api/users';
import { ALLOWED_NEXT_PAGES_REGEX, ROUTER_PAGES } from '@/utils/constants';

export function calculateTotalItems(quotationAnswer: IQuotationAnswer) {
  return quotationAnswer.answered_items?.reduce((acc, i) => {
    if (i.answered_compositions ? i.answered_compositions.length > 0 : false) {
      return (
        acc +
        (i.answered_compositions.reduce((acc2, j) => {
          return (
            acc2 + (j.unit_price_micros * j.total_quantity_micros) / 1000000
          );
        }, 0) *
          i.total_quantity_micros) /
          1000000
      );
    }
    return acc + (i.total_quantity_micros * i.unit_price_micros) / 1000000;
  }, 0);
}

export function calculateMergedTotalDiscount(
  mergedAnsweredItem: IMergedAnsweredItem
) {
  return formatMicroQuantity(
    mergedAnsweredItem.answered_items.reduce((acc, i) => {
      return acc + i.total_discount_micros;
    }, 0)
  );
}

export function calculateMergedTotalDiscountMicros(
  mergedAnsweredItem: IMergedAnsweredItem
) {
  return mergedAnsweredItem.answered_items.reduce((acc, i) => {
    return acc + i.total_discount_micros;
  }, 0);
}

export function calculateMergedTotalDiscountPercentMicros(
  mergedAnsweredItem: IMergedAnsweredItem
) {
  return Math.round(
    100 *
      (mergedAnsweredItem.answered_items.reduce((acc, i) => {
        return acc + i.total_discount_micros;
      }, 0) /
        Math.round(
          calculateTotalMergedAnsweredItemAmount(mergedAnsweredItem)
        )) *
      1000000
  );
}

export function mergedCalculateTotalItems(quotationAnswer: IQuotationAnswer) {
  return quotationAnswer.merged_answered_items?.reduce((acc, i) => {
    return (
      acc +
      i.answered_items.reduce((acc2, j) => {
        if (
          j.answered_compositions ? j.answered_compositions.length > 0 : false
        ) {
          return (
            acc2 +
            (j.answered_compositions.reduce((acc3, k) => {
              return (
                acc3 + (k.unit_price_micros * k.total_quantity_micros) / 1000000
              );
            }, 0) *
              j.total_quantity_micros) /
              1000000
          );
        }
        return acc2 + (j.total_quantity_micros * j.unit_price_micros) / 1000000;
      }, 0)
    );
  }, 0);
}

export function calculateTotalAmount(quotationAnswer: IQuotationAnswer) {
  return quotationAnswer.answered_items?.reduce((acc, i) => {
    if (i.answered_compositions ? i.answered_compositions.length > 0 : false) {
      return (
        acc -
        i.total_discount_micros +
        (i.answered_compositions.reduce((acc2, j) => {
          return (
            acc2 + (j.unit_price_micros * j.total_quantity_micros) / 1000000
          );
        }, 0) *
          i.total_quantity_micros) /
          1000000
      );
    }
    return (
      acc +
      (i.total_quantity_micros * i.unit_price_micros) / 1000000 -
      i.total_discount_micros
    );
  }, 0);
}

export function calculateTotalDiscount(quotationAnswer: IQuotationAnswer) {
  return quotationAnswer.answered_items?.reduce(
    (acc: number, i: IAnsweredItem) => {
      return i.total_discount_micros + acc;
    },
    0
  );
}

export function mergedCalculateTotalDiscount(
  quotationAnswer: IQuotationAnswer
) {
  return quotationAnswer.merged_answered_items?.reduce((acc, i) => {
    return (
      acc +
      i.answered_items.reduce((acc2, j) => {
        return j.total_discount_micros + acc2;
      }, 0)
    );
  }, 0);
}

export function calculateTotalFreight(quotationAnswer: IQuotationAnswer) {
  return quotationAnswer.answered_shipping_addresses
    ? quotationAnswer.answered_shipping_addresses.reduce(
        (acc: number, i: IAnsweredShippingAddress) => {
          return i.total_freight_micros + acc;
        },
        0
      )
    : 0;
}

export const calculateTotalItemsPerShippingAddress = (
  quotationAnswer: IQuotationAnswer,
  shippingAddress: IShippingAddress
) => {
  return quotationAnswer.answered_items.reduce((acc, current_item) => {
    if (current_item.item.shipping_address.id === shippingAddress.id) {
      if (current_item.answered_compositions.length !== 0) {
        return (
          (acc +
            (current_item.answered_compositions.reduce(
              (acc2, current_composition) => {
                return (
                  acc2 +
                  current_composition.unit_price_micros *
                    current_composition.total_quantity_micros
                );
              },
              0
            ) /
              1000000) *
              current_item.total_quantity_micros) /
            1000000 -
          current_item.total_discount_micros
        );
      } else {
        return (
          acc +
          (current_item.unit_price_micros *
            current_item.total_quantity_micros) /
            1000000 -
          current_item.total_discount_micros
        );
      }
    }
    return acc;
  }, 0);
};

export function formatchatdatetime(datetime: string) {
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    }).format(new Date(datetime));
  } catch (e) {
    return new Intl.DateTimeFormat('pt-BR', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    }).format(new Date());
  }
}

export function formatshortdate(datetime: string) {
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).format(new Date(datetime));
  } catch (e) {
    return new Intl.DateTimeFormat('pt-BR', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).format(new Date());
  }
}

export function formatshortdatehour(datetime: string) {
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false,
    }).format(new Date(datetime));
  } catch (e) {
    return new Intl.DateTimeFormat('pt-BR', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    }).format(new Date());
  }
}

export function formatdatetimestring(s: string) {
  if (!s) return;
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'long' }).format(
    new Date(s)
  );
}

export function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value ?? 0);
}

export function formatMicroCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value / 1000000 || 0);
}

export function formatMicroValueWithoutSymbol(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 1000000 || 0);
}

export function formatMicroQuantity(value: number) {
  return value / 1000000;
}

export function formatMicroQuantityDisplay(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 6,
  }).format(formatMicroQuantity(value));
}

export function calculateTotalMergedAnsweredItemAmount(
  mergedAnsweredItem: IMergedAnsweredItem
) {
  return mergedAnsweredItem.answered_items[0].answered_compositions
    ? mergedAnsweredItem.answered_items[0].answered_compositions.length > 0
      ? (mergedAnsweredItem.answered_items.reduce((acc, i) => {
          return acc + i.total_quantity_micros;
        }, 0) /
          1000000) *
        mergedAnsweredItem.answered_items[0].answered_compositions.reduce(
          (acc, j) => {
            return (
              acc + (j.total_quantity_micros * j.unit_price_micros) / 1000000
            );
          },
          0
        )
      : mergedAnsweredItem.answered_items.reduce((acc, k) => {
          return (
            acc + (k.total_quantity_micros * k.unit_price_micros) / 1000000
          );
        }, 0)
    : mergedAnsweredItem.answered_items.reduce((acc, k) => {
        return acc + (k.total_quantity_micros * k.unit_price_micros) / 1000000;
      }, 0);
}

export function calculateTotalAnsweredItemAmount(answeredItem: IAnsweredItem) {
  return answeredItem.answered_compositions
    ? answeredItem.answered_compositions.length > 0
      ? (answeredItem.total_quantity_micros *
          answeredItem.answered_compositions.reduce((acc, i) => {
            return (
              acc + (i.total_quantity_micros * i.unit_price_micros) / 1000000
            );
          }, 0)) /
        1000000
      : (answeredItem.unit_price_micros * answeredItem.total_quantity_micros) /
        1000000
    : (answeredItem.unit_price_micros * answeredItem.total_quantity_micros) /
        1000000;
}

export function calculateAnsweredItemPrice(answeredItem: IAnsweredItem) {
  return answeredItem.answered_compositions.length > 0
    ? answeredItem.answered_compositions.reduce(
        (acc, current_composition) =>
          acc +
          current_composition.unit_price_micros *
            current_composition.total_quantity_micros,
        0
      ) / 1000000
    : answeredItem.unit_price_micros;
}

export function calculateTotalItem(answeredItem: IAnsweredItem) {
  return (
    (calculateAnsweredItemPrice(answeredItem) *
      answeredItem.total_quantity_micros) /
      1000000 -
    answeredItem.total_discount_micros
  );
}

export function calculateTotalItemWithoutDiscount(answeredItem: IAnsweredItem) {
  return (
    (calculateAnsweredItemPrice(answeredItem) *
      answeredItem.total_quantity_micros) /
    1000000
  );
}

export function hasDiscountPerItem(quotationAnswer: IQuotationAnswer) {
  const percentageDiscount = quotationAnswer.answered_items.reduce(
    (acc, { total_discount_micros, unit_price_micros, available }) => {
      if (!available) return acc;
      return unit_price_micros > 0 && total_discount_micros > 0
        ? acc.concat([unit_price_micros / total_discount_micros])
        : acc.concat([0]);
    },
    [] as number[]
  );
  return !percentageDiscount.every(d => d === percentageDiscount[0]);
}

export const priorityUserName = (value: User) => {
  return value?.display_name || value?.name || '-';
};

export const hasRoutersHiddenNavBar = (
  router: NextRouter,
  isAuthenticated?: boolean
) => {
  return (
    router.route === ROUTER_PAGES.login() ||
    router.route === ROUTER_PAGES.cadastro ||
    router.route === ROUTER_PAGES.esqueciMinhaSenha() ||
    router.route.includes(ROUTER_PAGES.resetarSenha) ||
    (router.route.includes(ROUTER_PAGES.site) &&
      !/editar/.test(router.route) &&
      !isAuthenticated)
  );
};

export const hasRoutersHiddenSideBar = (
  router: NextRouter,
  isAuthenticated?: boolean
) => {
  return (
    router.route === ROUTER_PAGES.primeiroAcesso ||
    router.route === ROUTER_PAGES.login() ||
    router.route === ROUTER_PAGES.cadastro ||
    router.route === ROUTER_PAGES.esqueciMinhaSenha() ||
    router.route.includes(ROUTER_PAGES.resetarSenha) ||
    (router.route.includes(ROUTER_PAGES.site) &&
      !/editar/.test(router.route) &&
      !isAuthenticated)
  );
};

export const generateShippingAddress = (identification: number) => ({
  id: identification,
  construction_name: `Construção ${identification}`,
  street: `Rua ${identification}`,
  number: `${identification}`,
  city: `Cidade ${identification}`,
  zipcode: `${identification}`,
  state: `Estado ${identification}`,
  neighbourhood: `Bairro ${identification}`,
  complement: `Complemento ${identification}`,
});

export function paramsSerializer(params: ObjectLiteral) {
  return Object.keys(params || {})
    .map(key => {
      const paramValue = params[key];
      if (paramValue instanceof Array) {
        // arrayQuery = [1, 2, 3, 4] -> ?arrayQuery=1&arrayQuery=2&arrayQuery=3&arrayQuery=4
        return paramValue
          ?.map(value => `${key}=${encodeURIComponent(value)}`)
          .join('&');
      }

      return `${key}=${encodeURIComponent(
        paramValue as string | number | boolean
      )}`;
    })
    .join('&');
}

export function getLocationLabel(location: ILocation) {
  const possibleLocationLabels = {
    [LocationType.CITY]: () => `${location.city.name} - ${location.state.name}`,
    [LocationType.STATE]: () => `${location.state.name} - Estado`,
    [LocationType.REGION]: () => `${location.region.name} - Região`,
    [LocationType.COUNTRY]: () => `${location.country.name} - País`,
  };

  return possibleLocationLabels[location.type]();
}

export const isAllowedNextUrl = (url: string) => {
  return Object.values(ALLOWED_NEXT_PAGES_REGEX).some(regexValidator =>
    regexValidator.test(decodeURIComponent(url))
  );
};

export const hasParamsConfigureNotifications = (
  params: string | string[] | undefined
) => {
  return params === 'true';
};

export const generateWhatsappUrl = (number: string, text?: string) => {
  if (number) {
    if (text) {
      return `https://wa.me/${number}?text=${encodeURIComponent(text)}`;
    }
    return `https://wa.me/${number}`;
  }
  return '';
};

export function calculateMergedItemDiscountedValue(
  mergedItem: IMergedAnsweredItem
) {
  const { total_discounted_price_micros, total_quantity_micros } =
    mergedItem.answered_items.reduce(
      (acc, item) => {
        const totalItemDiscounted =
          (item.unit_price_micros * item.total_quantity_micros) / 1000000 -
          item.total_discount_micros;

        acc.total_discounted_price_micros += totalItemDiscounted;
        acc.total_quantity_micros += item.total_quantity_micros;

        return acc;
      },
      { total_discounted_price_micros: 0, total_quantity_micros: 0 }
    );

  return (
    Math.trunc(total_discounted_price_micros / total_quantity_micros) * 1000000
  );
}

export const getFilenameFromUrl = (url: string | null | undefined) => {
  if (!url) return;

  return url.replace(/^.*\//, '').replace(/[?#].*$/, '');
};

export const hasCNPJCompany = (quotationAnswer: IQuotationAnswer) => {
  return (
    quotationAnswer.quotation.company.cnpj &&
    quotationAnswer.quotation.company.cnpj !== ''
  );
};

export const shouldShowSignUpModal = (
  isAuthenticated: boolean,
  quotationAnswer: IQuotationAnswer
) => {
  return (
    !isAuthenticated &&
    !quotationAnswer.supplier &&
    quotationAnswer.quotation.foreign_plan?.toLowerCase() === 'shakespeare'
  );
};
